import { useCallback, useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { listAudit, type AuditEntry } from '@/lib/operatorApi';
import { formatDateTime } from '@/lib/format';

export default function Audit() {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [entity, setEntity] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    listAudit({ entity: entity || undefined, limit: 200 })
      .then((r) => setEntries(r.results))
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, [entity]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <PageHeader
        eyebrow="Аудит"
        title="Действия операторов"
        description="Все привилегированные действия логируются: вход «как владелец», возвраты, отзыв устройств, продление trial."
        actions={
          <button type="button" className="btn-ghost" onClick={load} disabled={loading}>
            <RefreshCw size={13} strokeWidth={1.75} />
            Обновить
          </button>
        }
      />

      <div className="page-pad">
        {error && (
          <div className="mb-4 border border-red-600/30 bg-red-50 px-4 py-3 text-[13px] text-red-700">
            {error}
          </div>
        )}

        <div className="mb-4 flex flex-wrap gap-1">
          {['', 'Establishment', 'Subscription', 'Device', 'SupportTicket', 'PlatformSetting'].map((e) => (
            <button
              key={e || 'all'}
              type="button"
              onClick={() => setEntity(e)}
              className={
                'border px-3 py-1.5 text-[12px] font-medium transition ' +
                (entity === e
                  ? 'border-ink bg-ink text-white'
                  : 'border-line bg-white text-ink/70 hover:border-ink/50')
              }
            >
              {e || 'Все'}
            </button>
          ))}
        </div>

        {/* Desktop: таблица */}
        <div className="card hidden md:block">
          <table className="table-grid">
            <thead>
              <tr>
                <th>Оператор</th>
                <th>Действие</th>
                <th>Объект</th>
                <th>ID объекта</th>
                <th>IP</th>
                <th className="text-right">Когда</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((a) => (
                <tr key={a.id}>
                  <td className="font-medium">
                    {a.actor?.email ?? a.actor?.name ?? 'система'}
                  </td>
                  <td>
                    <span className="font-mono text-[12px] text-ink/80">{a.action}</span>
                  </td>
                  <td className="text-ink/80">{a.entity}</td>
                  <td className="font-mono text-[11px] text-ink/50">
                    {a.entityId ? a.entityId.slice(0, 12) : '—'}
                  </td>
                  <td className="font-mono text-[12px] text-ink/60">{a.ip ?? '—'}</td>
                  <td className="text-right font-mono text-[12px] text-ink/60">
                    {formatDateTime(a.createdAt)}
                  </td>
                </tr>
              ))}
              {!loading && entries.length === 0 && (
                <tr>
                  <td colSpan={6} className="py-10 text-center text-ink/50">
                    Записей аудита нет
                  </td>
                </tr>
              )}
              {loading && entries.length === 0 && (
                <tr>
                  <td colSpan={6} className="py-10 text-center text-ink/40">
                    Загрузка…
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile: карточки */}
        <ul className="card divide-y divide-line md:hidden">
          {entries.map((a) => (
            <li key={a.id} className="px-4 py-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[13px] font-medium text-ink">
                    {a.actor?.email ?? a.actor?.name ?? 'система'}
                  </div>
                  <div className="mt-0.5 font-mono text-[12px] text-ink/80">{a.action}</div>
                </div>
                <div className="shrink-0 text-right font-mono text-[11px] text-ink/50">
                  {formatDateTime(a.createdAt)}
                </div>
              </div>
              <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-ink/60">
                <span>
                  <span className="text-ink/40">объект:</span> {a.entity}
                </span>
                {a.entityId && (
                  <span className="font-mono">
                    <span className="text-ink/40">id:</span> {a.entityId.slice(0, 10)}
                  </span>
                )}
                {a.ip && (
                  <span className="font-mono">
                    <span className="text-ink/40">ip:</span> {a.ip}
                  </span>
                )}
              </div>
            </li>
          ))}
          {!loading && entries.length === 0 && (
            <li className="px-4 py-10 text-center text-[13px] text-ink/50">Записей аудита нет</li>
          )}
          {loading && entries.length === 0 && (
            <li className="px-4 py-10 text-center text-[13px] text-ink/40">Загрузка…</li>
          )}
        </ul>
      </div>
    </div>
  );
}
